import React from "react";
import {
    Badge,
    Box,
    Button,
    Columns,
    Column,
    Divider,
    Heading,
    Inline,
    Stack,
    Tab,
    TabPanel,
    TabPanels,
    Tabs,
    TabsProvider,
    Text,
} from "braid-design-system";
import { BomIngredientRow, BomMenuItem } from "../../types/bom";
import BomIngredientsTable from "./BomIngredientsTable";
import BomVersionHistory from "./BomVersionHistory";
import ItemThumbnail from "./ItemThumbnail";

interface BomDetailPanelProps {
    item: BomMenuItem;
    draftIngredients: BomIngredientRow[];
    draftVersion: number;
    isDirty: boolean;
    canEdit: boolean;
    onAddIngredient: () => void;
    onEditIngredient: (row: BomIngredientRow) => void;
    onDeleteIngredient: (rowId: string) => void;
    onCancel: () => void;
    onDuplicate: () => void;
    onSave: () => void;
}

const ComingSoonPanel: React.FC<{ label: string }> = ({ label }) => (
    <Box padding="large" background="neutralLight" borderRadius="standard">
        <Text tone="secondary" align="center">
            {`${label} - coming soon.`}
        </Text>
    </Box>
);

const BomDetailPanel: React.FC<BomDetailPanelProps> = ({
    item,
    draftIngredients,
    draftVersion,
    isDirty,
    canEdit,
    onAddIngredient,
    onEditIngredient,
    onDeleteIngredient,
    onCancel,
    onDuplicate,
    onSave,
}) => {
    const [activeTab, setActiveTab] = React.useState("ingredients");

    return (
        <Box background="surface" borderRadius="standard" boxShadow="borderNeutralLight" padding="large">
            <Stack space="large">
                <Columns space="medium" collapseBelow="tablet">
                    <Column width="content">
                        <ItemThumbnail imageUrl={item.image_url} alt={item.item_name} size={88} fontSize={44} />
                    </Column>
                    <Column>
                        <Stack space="small">
                            <Inline space="small" alignY="center">
                                <Heading level="3">{item.item_name}</Heading>
                                <Badge tone="positive">
                                    {item.item_type === "FINISHED" ? "Finished Item" : "Semi Finished"}
                                </Badge>
                                {isDirty ? <Badge tone="caution">Unsaved changes</Badge> : null}
                            </Inline>
                            <Columns space="large" collapseBelow="tablet">
                                <Column>
                                    <Stack space="xsmall">
                                        <Text size="small" tone="secondary">{`Item Code : ${item.item_code}`}</Text>
                                        <Text size="small" tone="secondary">{`Item Type : ${item.item_type}`}</Text>
                                        <Text size="small" tone="secondary">{`UOM : ${item.uom}`}</Text>
                                    </Stack>
                                </Column>
                                <Column>
                                    <Stack space="xsmall">
                                        <Text size="small" tone="secondary">{`Yield Qty : ${item.yield_qty}`}</Text>
                                        <Text size="small" tone="secondary">{`Version : ${draftVersion}`}</Text>
                                    </Stack>
                                </Column>
                            </Columns>
                        </Stack>
                    </Column>
                </Columns>

                <Divider />

                <TabsProvider selectedItem={activeTab} onChange={(_index, item) => item && setActiveTab(item)}>
                    <Tabs label="BOM Detail Tabs">
                        <Tab item="ingredients">BOM Ingredients</Tab>
                        <Tab item="details">Details</Tab>
                        <Tab item="costing">Costing</Tab>
                        <Tab item="history">History</Tab>
                    </Tabs>
                    <TabPanels>
                        <TabPanel item="ingredients">
                            <Box paddingTop="large">
                                <BomIngredientsTable
                                    ingredients={draftIngredients}
                                    canEdit={canEdit}
                                    onAddIngredient={onAddIngredient}
                                    onEditIngredient={onEditIngredient}
                                    onDeleteIngredient={onDeleteIngredient}
                                />
                            </Box>
                        </TabPanel>
                        <TabPanel item="details">
                            <Box paddingTop="large">
                                <ComingSoonPanel label="Item details" />
                            </Box>
                        </TabPanel>
                        <TabPanel item="costing">
                            <Box paddingTop="large">
                                <ComingSoonPanel label="Costing breakdown" />
                            </Box>
                        </TabPanel>
                        <TabPanel item="history">
                            <Box paddingTop="large">
                                <BomVersionHistory itemCode={item.item_code} active={activeTab === "history"} />
                            </Box>
                        </TabPanel>
                    </TabPanels>
                </TabsProvider>

                {canEdit ? (
                    <>
                        <Divider />
                        <Box display="flex" justifyContent="flexEnd">
                            <Inline space="small">
                                <Button variant="soft" tone="neutral" onClick={isDirty ? onCancel : undefined}>
                                    Cancel
                                </Button>
                                <Button variant="ghost" tone="neutral" onClick={onDuplicate}>
                                    Duplicate BOM
                                </Button>
                                <Button tone="brandAccent" onClick={isDirty ? onSave : undefined}>
                                    Save BOM
                                </Button>
                            </Inline>
                        </Box>
                    </>
                ) : null}
            </Stack>
        </Box>
    );
};

export default BomDetailPanel;
